import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import InputText from '@components/core/input-text/input-text.tsx';
import Button from '@components/core/button/button.tsx';
import { BUTTON_STYLE } from '@components/core/button/button-style-enum.ts';
import { useLoaderData } from 'react-router';
import { INPUT_TEXT_TYPES } from '@components/core/input-text/input-text-enum.ts';
import Form from '@components/core/form/form.tsx';
import NavigationButton from '@components/navigation/navigation-button/navigation-button.tsx';
import LoadingIndicator from '@components/core/loading-indicator/loading-indicator.tsx';
import ViewPanel from '@components/view/view-panel.tsx';
import ClassString from '@utils/class-string.ts';
import useScreenBreakpoint from '@hooks/use-screen-breakpoint.tsx';
import { ScreenBreakpoint } from '@/enums/screen-breakpoints.ts';
import navSidebarStore from '@store/nav-sidebar-store.ts';
import AppLogo from '@components/core/app-logo/app-logo.tsx';
import authStore from '@store/auth-store.ts';

const ChangePassword = observer(function ChangePassword() {
	const token = useLoaderData<string>();

	const screenBreakpoint = useScreenBreakpoint();
	const [fadeIn, setFadeIn] = useState(false);

	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [loading, setLoading] = useState(false);

	const [showIncomplete, setShowIncomplete] = useState(false);

	const [changeSuccessful, setChangeSuccessful] = useState(false);

	document.title = 'Change Password | UGC Insight';

	const passwordsNotMatching =
		!!password.length &&
		!!confirmPassword.length &&
		password !== confirmPassword;

	const incomplete =
		password.length < 8 ||
		confirmPassword.length < 8 ||
		passwordsNotMatching;

	const handlePasswordChange = async () => {
		if (incomplete) {
			setShowIncomplete(true);
			return;
		}

		try {
			setLoading(true);
			const res = await authStore.changePassword(password, token);
			if (res.ok) {
				setChangeSuccessful(true);
			}
		} catch (_) {
			// Toast message will inform user of error
		}

		setPassword('');
		setConfirmPassword('');
		setLoading(false);
	};

	useEffect(() => {
		setFadeIn(true);
	}, []);

	return (
		<div
			className={
				'w-screen h-dvh bg-green-100 dark:bg-gray-575 sm:bg-transparent dark:sm:bg-transparent grid place-items-center overflow-y-auto sm:bg-top-page-pattern sm:dark:bg-none bg-cover'
			}
		>
			<div
				className={
					'flex flex-col justify-center items-center gap-4 h-full p-4'
				}
			>
				<ViewPanel
					className={ClassString({
						static: 'max-w-md opacity-0 transition-opacity bg-transparent sm:bg-green-100 pb-8',
						dynamic: { 'opacity-100': fadeIn },
					})}
					options={{
						container: screenBreakpoint <= ScreenBreakpoint.SM,
					}}
				>
					<div
						className={ClassString({
							static: 'flex flex-col justify-center items-center px-4',
							dynamic: {
								'opacity-0': loading,
							},
						})}
					>
						<AppLogo
							className={ClassString({
								static: 'flex flex-col justify-center items-center dark:text-white pt-0 pb-4 select-none flex-1',
								dynamic: {
									'lg:hidden': navSidebarStore.minimal,
								},
							})}
						/>

						{!changeSuccessful && (
							<>
								<div
									className={
										'w-full text-xl mb-4 text-center'
									}
								>
									Change Password
								</div>

								<Form
									className={
										'flex flex-col justify-center items-center w-full'
									}
									onSubmit={() => {
										void handlePasswordChange();
									}}
								>
									<div className={'relative w-full'}>
										<div
											className={ClassString({
												static: 'absolute -top-2 right-4 text-sm text-red-525 hidden',
												dynamic: {
													block:
														showIncomplete &&
														password.length < 8,
												},
											})}
										>
											{'Password needs to be longer'}
										</div>

										<InputText
											label={'New Password'}
											value={password}
											name={'password'}
											onInput={(event) =>
												setPassword(event.target.value)
											}
											type={INPUT_TEXT_TYPES.PASSWORD}
											options={{
												width: '100%',
												large: true,
											}}
											inputClassName={ClassString({
												static: 'bg-transparent',
												dynamic: {
													'outline-red-525':
														passwordsNotMatching ||
														(showIncomplete &&
															password.length <
																8),
												},
											})}
										/>
									</div>

									<div className={'relative w-full mb-2'}>
										<div
											className={ClassString({
												static: 'absolute -top-2 right-4 text-sm text-red-525 hidden',
												dynamic: {
													block: passwordsNotMatching,
												},
											})}
										>
											{'Password does not match'}
										</div>

										<InputText
											label={'Confirm Password'}
											value={confirmPassword}
											name={'confirm_email'}
											onInput={(event) =>
												setConfirmPassword(
													event.target.value
												)
											}
											type={INPUT_TEXT_TYPES.PASSWORD}
											options={{
												width: '100%',
												large: true,
											}}
											inputClassName={ClassString({
												static: 'bg-transparent',
												dynamic: {
													'outline-red-525':
														passwordsNotMatching ||
														(showIncomplete &&
															confirmPassword.length <
																8),
												},
											})}
										/>
									</div>

									<Button
										title={'Change Password'}
										buttonStyle={BUTTON_STYLE.PRIMARY}
										className={
											'mt-2 w-full py-6 rounded-full max-w-80'
										}
									/>
								</Form>
							</>
						)}

						{changeSuccessful && (
							<>
								<div className={'mb-6'}>
									Password change successful!
								</div>

								<NavigationButton
									path={'/sign-in'}
									title={'Continue'}
									className={
										'mt-2 w-full py-6 rounded-full max-w-80 justify-center bg-gray-600 text-white'
									}
									background
								/>
							</>
						)}
					</div>

					<div
						className={ClassString({
							static: 'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none',
							dynamic: { 'opacity-0': !loading },
						})}
					>
						<LoadingIndicator />
					</div>
				</ViewPanel>
			</div>
		</div>
	);
});

export default ChangePassword;
